import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { Upload, Plus, Pencil, Trash2, Download, Search, Lock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY = {
  document_number: '', document_title: '', discipline: '', discipline_code: '',
  doc_type: '', review_class: '', project_code: '',
  ifr_planned_date: '', ifa_planned_date: '', ifc_planned_date: '',
  originator: '', checker: '', approver: '',
  contractor: '', end_user: '', description: '', remarks: '',
};

export default function MasterRegister({ user, selectedProject, setSelectedProject, projects }) {
  const isSuperAdmin = user?.is_super_admin || (user?.role || '').toLowerCase() === 'admin';
  const canView = isSuperAdmin || user?.permissions?.can_view_mdr;
  const canManage = isSuperAdmin || user?.permissions?.can_manage_mdr;

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('entries');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importMode, setImportMode] = useState('upsert');
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().substring(0, 10));
  const [vsActual, setVsActual] = useState({ rows: [], not_in_mdr: [] });
  const [summary, setSummary] = useState(null);
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState('');

  const loadPackages = useCallback(async () => {
    if (!selectedProject) { setPackages([]); return; }
    try {
      const res = await axios.get(`${API}/master-register/${selectedProject}/packages`);
      setPackages(res.data.packages || []);
    } catch (e) {
      // silent — dropdown just stays empty
    }
  }, [selectedProject]);

  useEffect(() => { loadPackages(); }, [loadPackages]);

  const loadVsActual = async () => {
    if (!selectedProject) return toast.error('Select a project first');
    try {
      const res = await axios.get(`${API}/master-register/${selectedProject}/vs-actual`,
        { params: { as_of: asOfDate, package: selectedPackage || undefined } });
      setVsActual(res.data);
      toast.success(`Loaded ${res.data.rows?.length || 0} rows${selectedPackage ? ' for ' + selectedPackage : ''}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load reconciliation');
    }
  };

  const loadSummary = async () => {
    if (!selectedProject) return toast.error('Select a project first');
    try {
      const res = await axios.get(`${API}/master-register/${selectedProject}/summary`,
        { params: { as_of: asOfDate, package: selectedPackage || undefined } });
      setSummary(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load summary');
    }
  };

  const downloadReport = async () => {
    if (!selectedProject) return toast.error('Select a project first');
    try {
      const res = await axios.get(`${API}/master-register/${selectedProject}/export.xlsx`,
        { params: { as_of: asOfDate, package: selectedPackage || undefined }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const pkgSlug = (selectedPackage || 'AllPackages').replace(/[ /]/g, '-');
      a.download = `MDR_vs_Actual_${pkgSlug}_${asOfDate}.xlsx`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Failed to export report');
    }
  };

  const loadEntries = useCallback(async () => {
    if (!selectedProject || !canView) { setEntries([]); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/master-register/${selectedProject}`);
      setEntries(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load MDR entries');
    } finally {
      setLoading(false);
    }
  }, [selectedProject, canView]);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  // ----- Permission gate -----
  if (!canView) {
    return (
      <div className="p-6" data-testid="mdr-no-access">
        <div className="max-w-md mx-auto mt-16 border rounded-lg bg-white p-8 text-center">
          <Lock className="h-10 w-10 mx-auto text-gray-400 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900">Access Restricted</h2>
          <p className="text-sm text-gray-500 mt-2">
            You do not have permission to view the Master Document Register.
            Please ask an administrator to grant you the <code>View MDR</code> permission.
          </p>
        </div>
      </div>
    );
  }

  const openCreate = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (e) => { setEditing(e); setForm({ ...EMPTY, ...e }); setDialogOpen(true); };

  const submitForm = async () => {
    if (!selectedProject) return toast.error('Select a project first');
    if (!form.document_number?.trim()) return toast.error('Document Number is required');
    try {
      if (editing) {
        await axios.patch(`${API}/master-register/entry/${editing.id}`, form);
        toast.success('Entry updated');
      } else {
        await axios.post(`${API}/master-register/entry`,
          { ...form, project_id: selectedProject });
        toast.success('Entry created');
      }
      setDialogOpen(false);
      loadEntries();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const deleteEntry = async (entry) => {
    if (!window.confirm(`Delete MDR entry ${entry.document_number}?`)) return;
    try {
      await axios.delete(`${API}/master-register/entry/${entry.id}`);
      toast.success('Entry deleted');
      loadEntries();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/master-register/template.xlsx`,
        { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'MDR_Template.xlsx';
      a.click(); window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Failed to download template');
    }
  };

  const runImport = async () => {
    if (!selectedProject) return toast.error('Select a project first');
    if (!importFile) return toast.error('Choose an Excel file');
    const fd = new FormData();
    fd.append('file', importFile);
    fd.append('project_id', selectedProject);
    fd.append('mode', importMode);
    try {
      const res = await axios.post(`${API}/master-register/import`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      const d = res.data;
      toast.success(`Imported: ${d.inserted} new, ${d.updated} updated, ${d.skipped} skipped`);
      if (d.errors?.length) console.warn('MDR import errors:', d.errors);
      setImportOpen(false); setImportFile(null);
      loadEntries();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    }
  };

  const filtered = entries.filter((e) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (e.document_number || '').toLowerCase().includes(q)
      || (e.document_title || '').toLowerCase().includes(q)
      || (e.discipline || '').toLowerCase().includes(q)
      || (e.doc_type || '').toLowerCase().includes(q);
  });

  return (
    <div className="p-6 space-y-4" data-testid="mdr-page">
      {/* Inline project selector — visible without opening the sidebar */}
      <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2.5">
        <Label className="text-sm font-medium text-blue-900">Project:</Label>
        <select
          value={selectedProject || ''}
          onChange={(e) => setSelectedProject && setSelectedProject(e.target.value)}
          className="border rounded px-3 py-1.5 text-sm bg-white min-w-[280px]"
          data-testid="mdr-inline-project-select"
        >
          <option value="">-- Select a project --</option>
          <option value="all">All Projects (global comparison)</option>
          {(projects || []).map((p) => (
            <option key={p.id} value={p.id}>{p.name || p.code || p.id}</option>
          ))}
        </select>
        {!selectedProject && (
          <span className="text-xs text-blue-800">Pick a project to load its MDR entries.</span>
        )}
      </div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Master Document Register</h1>
          <p className="text-sm text-gray-500 mt-1">
            Planned document register vs. actual submissions
            {!canManage && <span className="ml-2 inline-block text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">Read-only</span>}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={downloadTemplate} data-testid="mdr-download-template">
            <Download className="h-4 w-4 mr-2" />Template
          </Button>
          {canManage && (
            <>
              <Button variant="outline" onClick={() => setImportOpen(true)}
                      disabled={!selectedProject} data-testid="mdr-import-btn">
                <Upload className="h-4 w-4 mr-2" />Import Excel
              </Button>
              <Button onClick={openCreate} disabled={!selectedProject}
                      data-testid="mdr-add-btn">
                <Plus className="h-4 w-4 mr-2" />Add Entry
              </Button>
            </>
          )}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="entries" data-testid="mdr-tab-entries">
            MDR Entries ({entries.length})
          </TabsTrigger>
          <TabsTrigger value="vs-actual" data-testid="mdr-tab-vs-actual">
            MDR vs Actual
          </TabsTrigger>
          <TabsTrigger value="summary" data-testid="mdr-tab-summary">
            Summary
          </TabsTrigger>
        </TabsList>

        <TabsContent value="entries" className="space-y-3">
          <div className="relative max-w-md">
            <Search className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
            <Input placeholder="Search by number, title, discipline..."
                   value={search} onChange={(e) => setSearch(e.target.value)}
                   className="pl-10" data-testid="mdr-search" />
          </div>
          <div className="border rounded-lg overflow-x-auto bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Document No.</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Discipline</TableHead>
                  <TableHead>Doc Type</TableHead>
                  <TableHead>IFR</TableHead>
                  <TableHead>IFA</TableHead>
                  <TableHead>IFC</TableHead>
                  <TableHead>Review Class</TableHead>
                  {canManage && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={canManage ? 9 : 8} className="text-center py-6 text-gray-400">Loading...</TableCell></TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={canManage ? 9 : 8} className="text-center py-6 text-gray-400">
                    {selectedProject ? (canManage ? 'No MDR entries yet. Click "Import Excel" or "Add Entry".' : 'No MDR entries for this project.') : 'Select a project to view entries.'}
                  </TableCell></TableRow>
                ) : filtered.map((e) => (
                  <TableRow key={e.id} data-testid={`mdr-row-${e.document_number}`}>
                    <TableCell className="font-mono text-xs">{e.document_number}</TableCell>
                    <TableCell className="max-w-xs truncate" title={e.document_title}>{e.document_title}</TableCell>
                    <TableCell>{e.discipline}</TableCell>
                    <TableCell>{e.doc_type}</TableCell>
                    <TableCell className="text-xs">{e.ifr_planned_date || '-'}</TableCell>
                    <TableCell className="text-xs">{e.ifa_planned_date || '-'}</TableCell>
                    <TableCell className="text-xs">{e.ifc_planned_date || '-'}</TableCell>
                    <TableCell>{e.review_class}</TableCell>
                    {canManage && (
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(e)}
                                data-testid={`mdr-edit-${e.document_number}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteEntry(e)}
                                data-testid={`mdr-delete-${e.document_number}`}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="vs-actual" className="space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <Label className="text-sm">As-of date:</Label>
            <Input type="date" value={asOfDate}
                   onChange={(e) => { setAsOfDate(e.target.value); }}
                   className="w-40" data-testid="mdr-asof" />
            <Button size="sm" variant="outline" onClick={loadVsActual}
                    data-testid="mdr-refresh-vs-actual">Refresh</Button>
            <Button size="sm" onClick={downloadReport} data-testid="mdr-export-report">
              <Download className="h-4 w-4 mr-2" />Export Excel Report
            </Button>
            <div className="ml-auto text-xs text-gray-500">
              {vsActual.rows?.length || 0} MDR rows • {vsActual.not_in_mdr?.length || 0} not in MDR
            </div>
          </div>
          <div className="border rounded-lg overflow-x-auto bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Document No.</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Discipline</TableHead>
                  <TableHead>Class</TableHead>
                  <TableHead>Critical</TableHead>
                  <TableHead>IFR</TableHead>
                  <TableHead>Actual Rev</TableHead>
                  <TableHead>Rev Date</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Delay</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(vsActual.rows || []).length === 0 ? (
                  <TableRow><TableCell colSpan={11} className="text-center py-6 text-gray-400">
                    {selectedProject ? 'Click "Refresh" to load reconciliation.' : 'Select a project first.'}
                  </TableCell></TableRow>
                ) : (vsActual.rows || []).map((r) => (
                  <TableRow key={r.id}
                    className={r.is_overdue ? 'bg-red-50' : (r.is_critical ? 'bg-yellow-50' : '')}
                    data-testid={`vs-row-${r.document_number}`}>
                    <TableCell className="font-mono text-xs">{r.document_number}</TableCell>
                    <TableCell className="max-w-xs truncate" title={r.document_title}>{r.document_title}</TableCell>
                    <TableCell className="text-xs">{r.discipline}</TableCell>
                    <TableCell className="text-xs">{r.review_class}</TableCell>
                    <TableCell>{r.is_critical ? <span className="text-orange-600 font-semibold">YES</span> : ''}</TableCell>
                    <TableCell className="text-xs">{r.ifr_planned_date || '-'}</TableCell>
                    <TableCell className="text-xs">{r.actual_revision || '-'}</TableCell>
                    <TableCell className="text-xs">{r.actual_revision_date ? String(r.actual_revision_date).substring(0, 10) : '-'}</TableCell>
                    <TableCell className="text-center">{r.code || '-'}</TableCell>
                    <TableCell className="text-xs text-right">{r.delay_days ?? '-'}</TableCell>
                    <TableCell className="text-xs">{r.status}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {(vsActual.not_in_mdr || []).length > 0 && (
            <>
              <h3 className="mt-6 text-lg font-semibold text-gray-800">
                Actual Documents NOT in MDR ({vsActual.not_in_mdr.length}) — DC review required
              </h3>
              <div className="border rounded-lg overflow-x-auto bg-white">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Document No.</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Discipline</TableHead>
                    <TableHead>Rev</TableHead>
                    <TableHead>Rev Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Code</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {vsActual.not_in_mdr.map((r) => (
                      <TableRow key={r.document_number} data-testid={`nim-row-${r.document_number}`}>
                        <TableCell className="font-mono text-xs">{r.document_number}</TableCell>
                        <TableCell className="max-w-xs truncate">{r.document_title}</TableCell>
                        <TableCell className="text-xs">{r.discipline}</TableCell>
                        <TableCell className="text-xs">{r.actual_revision}</TableCell>
                        <TableCell className="text-xs">{r.actual_revision_date ? String(r.actual_revision_date).substring(0, 10) : '-'}</TableCell>
                        <TableCell className="text-xs">{r.review_status}</TableCell>
                        <TableCell className="text-center">{r.code || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="summary" className="space-y-4">
          <div className="flex items-center gap-3">
            <Label className="text-sm">As-of date:</Label>
            <Input type="date" value={asOfDate}
                   onChange={(e) => setAsOfDate(e.target.value)}
                   className="w-40" data-testid="mdr-summary-asof" />
            <Button size="sm" variant="outline" onClick={loadSummary}
                    data-testid="mdr-refresh-summary">Refresh</Button>
          </div>
          {!summary ? (
            <div className="p-12 text-center text-gray-400 border rounded-lg bg-white">
              {selectedProject ? 'Click "Refresh" to load summary.' : 'Select a project first.'}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="p-4 bg-white border rounded-lg" data-testid="kpi-total">
                <div className="text-xs text-gray-500">Total MDR</div>
                <div className="text-3xl font-bold text-gray-900">{summary.total_mdr_entries}</div>
              </div>
              <div className="p-4 bg-white border rounded-lg" data-testid="kpi-submitted">
                <div className="text-xs text-gray-500">Submitted</div>
                <div className="text-3xl font-bold text-green-600">{summary.submitted}</div>
              </div>
              <div className="p-4 bg-white border rounded-lg" data-testid="kpi-overdue">
                <div className="text-xs text-gray-500">Overdue</div>
                <div className="text-3xl font-bold text-red-600">{summary.overdue}</div>
              </div>
              <div className="p-4 bg-white border rounded-lg" data-testid="kpi-nim">
                <div className="text-xs text-gray-500">Actual not in MDR</div>
                <div className="text-3xl font-bold text-orange-500">{summary.not_in_mdr_count}</div>
              </div>
              <div className="p-4 bg-white border rounded-lg" data-testid="kpi-critical">
                <div className="text-xs text-gray-500">Critical (Class 1/2)</div>
                <div className="text-3xl font-bold text-yellow-700">{summary.critical_total}</div>
                <div className="text-xs mt-1 text-red-500">{summary.critical_overdue} overdue</div>
              </div>
              <div className="p-4 bg-white border rounded-lg col-span-3">
                <div className="text-xs text-gray-500 mb-2">Approval codes</div>
                <div className="flex gap-4 flex-wrap text-sm">
                  <div>Code 1: <b className="text-green-700">{summary.code_1_approved}</b></div>
                  <div>Code 2: <b className="text-blue-700">{summary.code_2_approved_with_comments}</b></div>
                  <div>Code 3: <b className="text-red-700">{summary.code_3_rejected}</b></div>
                  <div>Code 4: <b className="text-gray-700">{summary.code_4_for_information}</b></div>
                  <div>Review pending: <b>{summary.review_pending}</b></div>
                </div>
              </div>
              <div className="p-4 bg-white border rounded-lg col-span-4">
                <div className="text-xs text-gray-500 mb-2">Ageing of overdue items</div>
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div className="p-3 bg-green-50 rounded"><div className="text-xs">0-30 days</div><div className="text-2xl font-bold">{summary.ageing_buckets['0-30']}</div></div>
                  <div className="p-3 bg-yellow-50 rounded"><div className="text-xs">31-60 days</div><div className="text-2xl font-bold">{summary.ageing_buckets['31-60']}</div></div>
                  <div className="p-3 bg-orange-50 rounded"><div className="text-xs">61-90 days</div><div className="text-2xl font-bold">{summary.ageing_buckets['61-90']}</div></div>
                  <div className="p-3 bg-red-50 rounded"><div className="text-xs">&gt;90 days</div><div className="text-2xl font-bold text-red-700">{summary.ageing_buckets['>90']}</div></div>
                </div>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {canManage && (
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="mdr-form-dialog">
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit MDR Entry' : 'Add MDR Entry'}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Document Number *</Label>
                <Input value={form.document_number} disabled={!!editing}
                       onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                       data-testid="mdr-form-doc-no" />
              </div>
              <div className="col-span-2">
                <Label>Document Title</Label>
                <Input value={form.document_title}
                       onChange={(e) => setForm({ ...form, document_title: e.target.value })} />
              </div>
              <div><Label>Discipline</Label>
                <Input value={form.discipline} onChange={(e) => setForm({ ...form, discipline: e.target.value })} /></div>
              <div><Label>Discipline Code</Label>
                <Input value={form.discipline_code} onChange={(e) => setForm({ ...form, discipline_code: e.target.value })} /></div>
              <div><Label>Doc Type</Label>
                <Input value={form.doc_type} onChange={(e) => setForm({ ...form, doc_type: e.target.value })} /></div>
              <div><Label>Review Class</Label>
                <Input value={form.review_class} onChange={(e) => setForm({ ...form, review_class: e.target.value })} /></div>
              <div><Label>Package / Project Code</Label>
                <Input list="mdr-pkg-list" value={form.project_code} onChange={(e) => setForm({ ...form, project_code: e.target.value })} data-testid="mdr-form-project-code" />
                <datalist id="mdr-pkg-list">
                  {(packages || []).map((p) => <option key={p} value={p} />)}
                </datalist>
              </div>
              <div><Label>IFR Planned Date</Label>
                <Input type="date" value={form.ifr_planned_date || ''} onChange={(e) => setForm({ ...form, ifr_planned_date: e.target.value })} /></div>
              <div><Label>IFA Planned Date</Label>
                <Input type="date" value={form.ifa_planned_date || ''} onChange={(e) => setForm({ ...form, ifa_planned_date: e.target.value })} /></div>
              <div><Label>IFC Planned Date</Label>
                <Input type="date" value={form.ifc_planned_date || ''} onChange={(e) => setForm({ ...form, ifc_planned_date: e.target.value })} /></div>
              <div><Label>Originator</Label>
                <Input value={form.originator} onChange={(e) => setForm({ ...form, originator: e.target.value })} /></div>
              <div><Label>Checker</Label>
                <Input value={form.checker} onChange={(e) => setForm({ ...form, checker: e.target.value })} /></div>
              <div><Label>Approver</Label>
                <Input value={form.approver} onChange={(e) => setForm({ ...form, approver: e.target.value })} /></div>
              <div><Label>Contractor</Label>
                <Input value={form.contractor} onChange={(e) => setForm({ ...form, contractor: e.target.value })} /></div>
              <div><Label>End User</Label>
                <Input value={form.end_user} onChange={(e) => setForm({ ...form, end_user: e.target.value })} /></div>
              <div className="col-span-2"><Label>Description</Label>
                <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div className="col-span-2"><Label>Remarks</Label>
                <Textarea rows={2} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={submitForm} data-testid="mdr-form-save">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {canManage && (
        <Dialog open={importOpen} onOpenChange={setImportOpen}>
          <DialogContent data-testid="mdr-import-dialog">
            <DialogHeader><DialogTitle>Import MDR from Excel</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="text-sm text-gray-600">
                Upload an Excel file with a sheet named <b>MASTER DOCUMENT REGISTER</b>.
                Required column: <code>DOCUMENT NO.</code>
              </div>
              <Input type="file" accept=".xlsx,.xls"
                     onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                     data-testid="mdr-import-file" />
              <div>
                <Label>Mode</Label>
                <select className="border rounded px-2 py-1 ml-2" value={importMode}
                        onChange={(e) => setImportMode(e.target.value)}>
                  <option value="upsert">Upsert (update existing, add new)</option>
                  <option value="replace">Replace (soft-delete all existing first)</option>
                </select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setImportOpen(false)}>Cancel</Button>
              <Button onClick={runImport} data-testid="mdr-import-run">Import</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
